import { useRef } from 'react';

import type { GradeBand } from './gradeScale.types';

import { getBandSpan } from './gradeScaleBands';
import { gradeScaleColorAt, gradeScalePalette } from './gradeScaleColors';

export interface GradeScalePreviewBarProps {
  bands: GradeBand[];
  /** Drag of the separator between band `index` and `index + 1`; sets its max %. */
  onMoveBoundary?: (index: number, maxPercent: number) => void;
  /** Double click on the bar; adds a separator at the clicked percent. */
  onAddSeparator?: (percent: number) => void;
}

export function GradeScalePreviewBar(gradeScalePreviewBarProps: GradeScalePreviewBarProps) {
  const { bands, onMoveBoundary, onAddSeparator } = gradeScalePreviewBarProps;
  const barRef = useRef<HTMLDivElement>(null);
  const palette: string[] = gradeScalePalette(bands.length);

  const percentFromClientX = (clientX: number): number | null => {
    const bar = barRef.current;
    if (!bar) {
      return null;
    }
    const rect: DOMRect = bar.getBoundingClientRect();
    return Math.round(((clientX - rect.left) / rect.width) * 100);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative h-4">
        {[0, 25, 50, 75, 100].map((mark) => (
          <span
            key={mark}
            className="text-muted-foreground absolute top-0 text-xs"
            style={{
              left: `${mark}%`,
              transform: mark === 0 ? 'translateX(0)' : mark === 100 ? 'translateX(-100%)' : 'translateX(-50%)',
            }}
          >
            {mark}%
          </span>
        ))}
      </div>

      <div
        ref={barRef}
        className="flex h-14 w-full items-stretch gap-1 select-none"
        onDoubleClick={(event) => {
          const percent: number | null = percentFromClientX(event.clientX);
          if (percent !== null) {
            onAddSeparator?.(percent);
          }
        }}
      >
        {bands.map((band, index) => {
          const span: number = getBandSpan(bands, index);
          const isLast: boolean = index === bands.length - 1;
          const color: string = palette[index] ?? gradeScaleColorAt(0);
          const nextBand = bands[index + 1];
          return (
            <div
              key={index}
              className="text-foreground relative flex min-w-0 flex-col items-center justify-center rounded-md px-2 text-center"
              style={{
                flexGrow: Math.max(span, 1),
                flexBasis: 0,
                backgroundColor: color,
                color: `contrast-color(${color})`,
              }}
            >
              <span className="truncate text-sm font-semibold">{band.name}</span>
              <span className="truncate text-xs opacity-70">
                {band.minPercent}-{band.maxPercent}%
              </span>
              {!isLast && nextBand && (
                <GradeScaleSeparatorHandle
                  value={band.maxPercent}
                  min={band.minPercent}
                  max={nextBand.maxPercent - 1}
                  percentFromClientX={percentFromClientX}
                  onMove={(boundary) => onMoveBoundary?.(index, boundary)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface GradeScaleSeparatorHandleProps {
  value: number;
  min: number;
  max: number;
  percentFromClientX: (clientX: number) => number | null;
  onMove: (boundary: number) => void;
}

function GradeScaleSeparatorHandle(gradeScaleSeparatorHandleProps: GradeScaleSeparatorHandleProps) {
  const { value, min, max, percentFromClientX, onMove } = gradeScaleSeparatorHandleProps;

  const moveTo = (boundary: number) => {
    onMove(Math.min(Math.max(boundary, min), max));
  };

  return (
    <button
      type="button"
      role="slider"
      aria-label="Granica oceny"
      aria-orientation="horizontal"
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuetext={`${value}%`}
      className="outline-focus-ring group absolute inset-y-0 -right-0.5 z-10 grid w-6 translate-x-1/2 cursor-ew-resize touch-none place-items-center rounded-md outline-0 focus-visible:outline-3"
      onDoubleClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
          return;
        }
        const percent: number | null = percentFromClientX(event.clientX);
        if (percent !== null) {
          moveTo(percent);
        }
      }}
      onKeyDown={(event) => {
        const step: number = event.shiftKey ? 10 : 1;
        const next: number | undefined = {
          ArrowLeft: value - step,
          ArrowDown: value - step,
          ArrowRight: value + step,
          ArrowUp: value + step,
          Home: min,
          End: max,
        }[event.key];
        if (next === undefined) {
          return;
        }
        event.preventDefault();
        moveTo(next);
      }}
    >
      <span
        className="border-border-primary bg-muted group-hover:border-border-primary-focus group-focus-visible:border-border-primary-focus h-8 w-1.5 rounded-full border-2 shadow-sm transition-[height,border-color] group-hover:h-10 group-focus-visible:h-10 group-active:h-10"
        aria-hidden
      />
    </button>
  );
}
